import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["checkbox", "selectAll", "count", "deleteButton", "form", "seriesSelect", "seriesButton", "seriesForm", "activateButton", "deactivateButton", "activateForm", "deactivateForm", "syncButton", "syncStatus", "syncImagesCheckbox"]
  static values = {
    syncUrl: String,
    productionUrl: String,
    apiKey: String
  }

  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly selectAllTarget: HTMLInputElement
  declare readonly countTargets: HTMLElement[]
  declare readonly deleteButtonTarget: HTMLButtonElement
  declare readonly formTarget: HTMLFormElement
  declare readonly seriesSelectTarget: HTMLSelectElement
  declare readonly seriesButtonTarget: HTMLButtonElement
  declare readonly seriesFormTarget: HTMLFormElement
  declare readonly activateButtonTarget: HTMLButtonElement
  declare readonly deactivateButtonTarget: HTMLButtonElement
  declare readonly activateFormTarget: HTMLFormElement
  declare readonly deactivateFormTarget: HTMLFormElement
  declare readonly syncButtonTarget: HTMLButtonElement
  declare readonly syncStatusTarget: HTMLElement
  declare readonly syncImagesCheckboxTarget: HTMLInputElement

  declare readonly hasSelectAllTarget: boolean
  declare readonly hasDeleteButtonTarget: boolean
  declare readonly hasSeriesButtonTarget: boolean
  declare readonly hasActivateButtonTarget: boolean
  declare readonly hasDeactivateButtonTarget: boolean
  declare readonly hasSyncButtonTarget: boolean
  declare readonly hasSyncStatusTarget: boolean
  declare readonly hasSyncImagesCheckboxTarget: boolean

  declare syncUrlValue: string
  declare productionUrlValue: string
  declare apiKeyValue: string

  connect(): void {
    this.updateUI()
  }

  toggleAll(): void {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = checked
    })
    this.updateUI()
  }

  toggle(event: Event): void {
    const target = event.target as HTMLInputElement
    const value = target.value
    const checked = target.checked

    // Sync other checkboxes with the same value (e.g. across grid/list views)
    this.checkboxTargets.forEach(cb => {
      if (cb.value === value && cb !== target) {
        cb.checked = checked
      }
    })

    this.updateUI()
  }

  updateUI(): void {
    // 고유 ID 기반으로 카운트 (그리드/리스트 뷰 중복 제거)
    const checkedIds = this.getUniqueCheckedIds()
    const checkedCount = checkedIds.length
    const totalCount = this.getUniqueTotalCount()

    // Update all count displays (there may be multiple)
    this.countTargets.forEach(countEl => {
      countEl.textContent = checkedCount.toString()
    })

    // Update select all checkbox
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = checkedCount === totalCount && totalCount > 0
      this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < totalCount
    }

    // Enable/disable delete button
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.disabled = checkedCount === 0
      if (checkedCount > 0) {
        this.deleteButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.deleteButtonTarget.classList.add("hover:bg-red-700")
      } else {
        this.deleteButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.deleteButtonTarget.classList.remove("hover:bg-red-700")
      }
    }

    // Enable/disable series button
    if (this.hasSeriesButtonTarget) {
      this.seriesButtonTarget.disabled = checkedCount === 0
      if (checkedCount > 0) {
        this.seriesButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.seriesButtonTarget.classList.add("hover:bg-indigo-700")
      } else {
        this.seriesButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.seriesButtonTarget.classList.remove("hover:bg-indigo-700")
      }
    }

    // Enable/disable activate button
    if (this.hasActivateButtonTarget) {
      this.activateButtonTarget.disabled = checkedCount === 0
      if (checkedCount > 0) {
        this.activateButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.activateButtonTarget.classList.add("hover:bg-emerald-700")
      } else {
        this.activateButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.activateButtonTarget.classList.remove("hover:bg-emerald-700")
      }
    }

    // Enable/disable deactivate button
    if (this.hasDeactivateButtonTarget) {
      this.deactivateButtonTarget.disabled = checkedCount === 0
      if (checkedCount > 0) {
        this.deactivateButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.deactivateButtonTarget.classList.add("hover:bg-amber-700")
      } else {
        this.deactivateButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.deactivateButtonTarget.classList.remove("hover:bg-amber-700")
      }
    }

    // Enable/disable sync button
    if (this.hasSyncButtonTarget) {
      this.syncButtonTarget.disabled = checkedCount === 0
      if (checkedCount > 0) {
        this.syncButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.syncButtonTarget.classList.add("hover:bg-cyan-700")
      } else {
        this.syncButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.syncButtonTarget.classList.remove("hover:bg-cyan-700")
      }
    }
  }

  confirmDelete(event: Event): void {
    const checkedCount = this.checkboxTargets.filter(cb => cb.checked).length
    if (checkedCount === 0) {
      event.preventDefault()
      alert("삭제할 로봇을 선택해주세요.")
      return
    }

    if (!confirm(`선택한 ${checkedCount}개의 로봇을 삭제하시겠습니까?`)) {
      event.preventDefault()
    }
  }

  submitDelete(): void {
    const checkedCount = this.checkboxTargets.filter(cb => cb.checked).length
    if (checkedCount === 0) {
      alert("삭제할 로봇을 선택해주세요.")
      return
    }

    if (confirm(`선택한 ${checkedCount}개의 로봇을 삭제하시겠습니까?`)) {
      this.formTarget.submit()
    }
  }

  submitSeriesChange(): void {
    const checkedCount = this.checkboxTargets.filter(cb => cb.checked).length
    if (checkedCount === 0) {
      alert("시리즈를 변경할 로봇을 선택해주세요.")
      return
    }

    const selectedSeries = this.seriesSelectTarget.value
    if (!selectedSeries) {
      alert("변경할 시리즈를 선택해주세요.")
      return
    }

    const seriesName = selectedSeries === "__clear__" ? "없음" : selectedSeries
    if (confirm(`선택한 ${checkedCount}개의 로봇 시리즈를 "${seriesName}"(으)로 변경하시겠습니까?`)) {
      // Copy checked robot_ids to series form
      const seriesForm = this.seriesFormTarget
      // Remove existing robot_ids inputs
      seriesForm.querySelectorAll('input[name="robot_ids[]"]').forEach(el => el.remove())
      // Add new robot_ids inputs
      this.checkboxTargets.filter(cb => cb.checked).forEach(cb => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'robot_ids[]'
        input.value = cb.value
        seriesForm.appendChild(input)
      })
      // Set the series value
      const newSeriesInput = seriesForm.querySelector('input[name="new_series"]') as HTMLInputElement
      if (newSeriesInput) {
        newSeriesInput.value = selectedSeries
      }
      seriesForm.submit()
    }
  }

  submitActivate(): void {
    const checkedCount = this.checkboxTargets.filter(cb => cb.checked).length
    if (checkedCount === 0) {
      alert("활성화할 로봇을 선택해주세요.")
      return
    }

    if (confirm(`선택한 ${checkedCount}개의 로봇을 활성화하시겠습니까? (이미지가 없는 로봇은 제외됩니다)`)) {
      const activateForm = this.activateFormTarget
      activateForm.querySelectorAll('input[name="robot_ids[]"]').forEach(el => el.remove())
      this.checkboxTargets.filter(cb => cb.checked).forEach(cb => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'robot_ids[]'
        input.value = cb.value
        activateForm.appendChild(input)
      })
      activateForm.submit()
    }
  }

  submitDeactivate(): void {
    const checkedCount = this.checkboxTargets.filter(cb => cb.checked).length
    if (checkedCount === 0) {
      alert("비활성화할 로봇을 선택해주세요.")
      return
    }

    if (confirm(`선택한 ${checkedCount}개의 로봇을 비활성화하시겠습니까?`)) {
      const deactivateForm = this.deactivateFormTarget
      deactivateForm.querySelectorAll('input[name="robot_ids[]"]').forEach(el => el.remove())
      this.checkboxTargets.filter(cb => cb.checked).forEach(cb => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'robot_ids[]'
        input.value = cb.value
        deactivateForm.appendChild(input)
      })
      deactivateForm.submit()
    }
  }

  async submitSync(): Promise<void> {
    const checkedIds = this.getUniqueCheckedIds()
    if (checkedIds.length === 0) {
      alert("동기화할 로봇을 선택해주세요.")
      return
    }

    const syncImages = this.hasSyncImagesCheckboxTarget && this.syncImagesCheckboxTarget.checked
    const imageText = syncImages ? " (이미지 포함)" : ""

    if (!confirm(`선택한 ${checkedIds.length}개의 로봇을 프로덕션 서버에 동기화하시겠습니까?${imageText}`)) {
      return
    }

    this.updateSyncStatus(syncImages ? "이미지 포함 동기화 중..." : "동기화 중...", "text-slate-500")
    this.syncButtonTarget.disabled = true

    try {
      // 로컬 서버에서 선택한 로봇 데이터 가져오기
      const response = await fetch(this.syncUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ robot_ids: checkedIds, sync_images: syncImages })
      })

      const result = await response.json()

      if (result.success) {
        this.updateSyncStatus(`✓ ${result.synced_count}개 동기화 완료`, "text-emerald-600")
        if (result.errors && result.errors.length > 0) {
          console.warn("Sync errors:", result.errors)
          this.updateSyncStatus(`✓ ${result.synced_count}개 완료, ${result.errors.length}개 실패`, "text-amber-600")
        }
      } else {
        this.updateSyncStatus(`✗ 동기화 실패: ${result.error}`, "text-red-600")
      }
    } catch (error) {
      console.error("Sync error:", error)
      this.updateSyncStatus(`✗ 오류: ${error}`, "text-red-600")
    } finally {
      this.syncButtonTarget.disabled = false
      this.updateUI()
    }
  }

  private getUniqueCheckedIds(): string[] {
    const ids = new Set<string>()
    this.checkboxTargets.filter(cb => cb.checked).forEach(cb => {
      ids.add(cb.value)
    })
    return Array.from(ids)
  }

  private getUniqueTotalCount(): number {
    const ids = new Set<string>()
    this.checkboxTargets.forEach(cb => {
      ids.add(cb.value)
    })
    return ids.size
  }

  private updateSyncStatus(message: string, colorClass: string): void {
    if (this.hasSyncStatusTarget) {
      this.syncStatusTarget.textContent = message
      this.syncStatusTarget.className = `text-sm ${colorClass}`
    }
  }

  private getCSRFToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? (meta.getAttribute('content') || '') : ''
  }
}
